"use client";

import type React from "react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Link, useLocation } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  Home,
  ImageIcon,
  LogOut,
  Moon,
  Sun,
  X,
  Layers,
  Settings,
  FolderKanban,
} from "lucide-react";
import { Logo } from "@/components/logo";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import ThemeToggle from "@/components/theme-toggle";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useCallback, memo } from "react";
import { PWAInstallPrompt } from "@/components/pwa-install-prompt";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

// Constants
const ANIMATION_CONFIG = {
  type: "spring",
  damping: 20,
  stiffness: 300,
};

// Types
interface NavItem {
  path: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface SidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Animation variants
const sidebarVariants = {
  open: { x: 0, transition: ANIMATION_CONFIG },
  closed: { x: "-100%", transition: ANIMATION_CONFIG },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.5 },
  exit: { opacity: 0 },
};

const contentVariants = {
  collapsed: { opacity: 0, scale: 0.8 },
  expanded: { opacity: 1, scale: 1 },
};

// Helper components
const NavItemIcon = memo(
  ({
    Icon,
    className,
  }: {
    Icon: React.ComponentType<{ className?: string }>;
    className?: string;
  }) => <Icon className={cn("h-5 w-5", className)} />
);
NavItemIcon.displayName = "NavItemIcon";

const ActiveIndicator = memo(({ isRTL }: { isRTL: boolean }) => (
  <motion.div
    layoutId="activeIndicator"
    className={cn(
      "absolute w-1 h-5 bg-primary rounded-full",
      isRTL ? "right-0 rounded-l-full" : "left-0 rounded-r-full"
    )}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
    aria-hidden="true"
  />
));
ActiveIndicator.displayName = "ActiveIndicator";

export const SidePanel = memo(
  ({
    isOpen,
    onClose,
    isCollapsed = false,
    onToggleCollapse = () => {},
  }: SidePanelProps) => {
    const { user, signOut } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const isMobile = useMobile();
    const panelRef = useRef<HTMLDivElement>(null);
    const { t, i18n } = useTranslation("common"); // Include i18n for language direction

    const isRTL = i18n.language === "ar"; // Determine if the current language is RTL

    // Constants
    const NAV_ITEMS = [
      { path: "/dashboard", label: t("common:sidePanel.dashboard"), icon: Home },
      {
        path: "/projects",
        label: t("common:sidePanel.projects"),
        icon: FolderKanban,
      },
      {
        path: "/previews",
        label: t("common:sidePanel.previews"),
        icon: ImageIcon,
      },
      { path: "/models", label: t("common:sidePanel.models"), icon: Layers },
    ] as const;

    // Memoized values
    const getInitials = useCallback((email: string) => {
      return email?.substring(0, 2).toUpperCase() || "U";
    }, []);

    const isActive = useCallback(
      (path: string) => {
        return location.pathname === path;
      },
      [location.pathname]
    );

    const handleSignOut = useCallback(() => {
      signOut();
      if (isMobile) onClose();
    }, [signOut, isMobile, onClose]);

    const handleThemeToggle = useCallback(() => {
      toggleTheme();
    }, [toggleTheme]);

    // Event handlers
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          isMobile &&
          isOpen &&
          panelRef.current &&
          !panelRef.current.contains(event.target as Node)
        ) {
          onClose();
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape" && isOpen && isMobile) {
          onClose();
        }
      };

      if (isMobile && isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
          document.removeEventListener("keydown", handleKeyDown);
        };
      }
    }, [isMobile, isOpen, onClose]);

    // Render helpers
    const renderCollapsedNavItem = useCallback(
      (item: NavItem) => (
        <Tooltip key={item.path}>
          <TooltipTrigger asChild>
            <Link
              to={item.path}
              className={cn(
                "flex items-center justify-center w-10 h-10 rounded-md hover:bg-accent transition-colors relative",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isActive(item.path) &&
                  "bg-accent text-accent-foreground font-medium"
              )}
              onClick={isMobile ? onClose : undefined}
              aria-current={isActive(item.path) ? "page" : undefined}
              aria-label={item.label}
            >
              <NavItemIcon Icon={item.icon} />
              {isActive(item.path) && <ActiveIndicator isRTL={true} />}
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      ),
      [isActive, isMobile, onClose, isRTL]
    );

    const renderExpandedNavItem = useCallback(
      (item: NavItem) => (
        <Link
          key={item.path}
          to={item.path}
          className={cn(
            "flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-accent transition-colors relative",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            isActive(item.path) &&
              "bg-accent text-accent-foreground font-medium"
          )}
          onClick={isMobile ? onClose : undefined}
          aria-current={isActive(item.path) ? "page" : undefined}
        >
          {isActive(item.path) && <ActiveIndicator isRTL={isRTL} />}
          <NavItemIcon Icon={item.icon} />
          <span>{item.label}</span>
        </Link>
      ),
      [isActive, isMobile, onClose, isRTL]
    );

    const isDesktopCollapsed = isCollapsed && !isMobile;
    const showExpandedContent = !isCollapsed || isMobile;

    return (
      <>
        {/* Mobile Overlay */}
        <AnimatePresence>
          {isMobile && isOpen && (
            <motion.div
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-0 bg-black z-40"
              onClick={onClose}
              aria-hidden="true"
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <motion.aside
          ref={panelRef}
          className={cn(
            "fixed inset-y-0 left-0 z-50 bg-background border-r shadow-lg flex flex-col",
            "md:translate-x-0 md:static md:z-0",
            isDesktopCollapsed ? "md:w-16" : "md:w-64"
          )}
          initial={isMobile ? "closed" : "open"}
          animate={isMobile ? (isOpen ? "open" : "closed") : "open"}
          variants={sidebarVariants}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Header */}
          <header className="flex items-center justify-between p-4 border-b">
            <Link
              to="/dashboard"
              className={cn(
                "flex items-center",
                isDesktopCollapsed ? "justify-center w-full" : "space-x-2"
              )}
            >
              <Logo className="h-14 text-primary flex-shrink-0" />
              <AnimatePresence>
                {showExpandedContent && (
                  <motion.span
                    variants={contentVariants}
                    initial="collapsed"
                    animate="expanded"
                    exit="collapsed"
                    className="font-bold text-primary"
                  >
                    3D Previewer
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>

            {isMobile ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
                aria-label="Close navigation sidebar"
              >
                <X className="h-5 w-5" />
              </Button>
            ) : (
              !isCollapsed && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onToggleCollapse}
                  aria-label="Collapse sidebar"
                >
                  {isRTL ? (
                    <ChevronRight className="h-5 w-5" />
                  ) : (
                    <ChevronLeft className="h-5 w-5" />
                  )}
                </Button>
              )
            )}
          </header>

          {/* Expand Button for Collapsed Desktop State */}
          {isDesktopCollapsed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleCollapse}
              className={cn(
                "absolute top-20 h-6 w-6 rounded-full border bg-background shadow-md hover:shadow-lg transition-shadow",
                isRTL ? "-left-3" : "-right-3"
              )}
              aria-label="Expand sidebar"
            >
              {isRTL ? (
                <ChevronLeft className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}

          {/* Profile Section */}
          <section
            className={cn(
              "p-4 border-b",
              isDesktopCollapsed && "flex justify-center"
            )}
            aria-label="User profile"
          >
            {isDesktopCollapsed ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to="/profile"
                      className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
                      aria-label={`Profile: ${
                        user?.user_metadata?.name || user?.email
                      }`}
                    >
                      <Avatar className="ring-2 ring-primary/20 transition-all hover:ring-primary/50">
                        <AvatarImage
                          src={
                            user?.user_metadata?.avatar_url ||
                            "/placeholder.svg"
                          }
                          alt=""
                        />
                        <AvatarFallback>
                          {getInitials(user?.email || "")}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    <p>{user?.user_metadata?.name || user?.email}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <Link
                to="/profile"
                className="flex items-center space-x-3 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md p-1 -m-1"
              >
                <Avatar className="ring-2 ring-primary/20 transition-all group-hover:ring-primary/50 group-focus:ring-primary/50">
                  <AvatarImage
                    src={user?.user_metadata?.avatar_url || "/placeholder.svg"}
                    alt=""
                  />
                  <AvatarFallback>
                    {getInitials(user?.email || "")}
                  </AvatarFallback>
                </Avatar>
                <div className="overflow-hidden min-w-0">
                  <p className="font-medium truncate">
                    {user?.user_metadata?.name || user?.email}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">
                    {user?.email}
                  </p>
                </div>
              </Link>
            )}
          </section>

          {/* Navigation */}
          <nav
            className="flex-1 overflow-y-auto p-4 space-y-2"
            aria-label="Main navigation"
          >
            {isDesktopCollapsed ? (
              <TooltipProvider>
                <div className="flex flex-col items-center space-y-2">
                  {NAV_ITEMS.map(renderCollapsedNavItem)}
                </div>
              </TooltipProvider>
            ) : (
              <div className="space-y-1">
                {NAV_ITEMS.map(renderExpandedNavItem)}
              </div>
            )}
          </nav>

          {/* Settings & Actions */}
          <footer
            className={cn(
              "p-4 border-t mt-auto",
              isDesktopCollapsed && "flex flex-col items-center space-y-4"
            )}
          >
            {isDesktopCollapsed ? (
              <TooltipProvider>
                <div className="flex flex-col items-center space-y-4">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <PWAInstallPrompt isCollapsed={true} />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t("common:sidePanel.install")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/profile"
                        className={cn(
                          "flex items-center justify-center w-10 h-10 rounded-md hover:bg-accent transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          isActive("/profile") &&
                            "bg-accent text-accent-foreground"
                        )}
                        onClick={isMobile ? onClose : undefined}
                        aria-label="Settings"
                      >
                        <Settings className="h-5 w-5" />
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t("common:sidePanel.settings")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <LanguageSwitcher />
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t("common:sidePanel.changeLanguage")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleThemeToggle}
                        aria-label={`Switch to ${
                          theme === "light" ? "dark" : "light"
                        } mode`}
                      >
                        {theme === "light" ? (
                          <Moon className="h-5 w-5" />
                        ) : (
                          <Sun className="h-5 w-5" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t("common:sidePanel.toggleTheme")}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={handleSignOut}
                        aria-label="Sign out"
                      >
                        <LogOut className="h-5 w-5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t("common:buttons.signout")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            ) : (
              <div className="space-y-4">
                <PWAInstallPrompt className="mb-4" />
                <div className="flex items-center justify-between">
                  <Link
                    to="/profile"
                    className={cn(
                      "flex items-center space-x-3 px-3 py-2 rounded-md hover:bg-accent transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      isActive("/profile") &&
                        "bg-accent text-accent-foreground font-medium"
                    )}
                    onClick={isMobile ? onClose : undefined}
                  >
                    <Settings className="h-5 w-5" />
                    <span>{t("common:sidePanel.settings")}</span>
                  </Link>
                  <LanguageSwitcher />
                  <ThemeToggle />
                </div>
                <Button
                  variant="destructive"
                  className="w-full flex items-center justify-center space-x-2"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("common:buttons.signout")}</span>
                </Button>
              </div>
            )}
          </footer>
        </motion.aside>
      </>
    );
  }
);

SidePanel.displayName = "SidePanel";
